/**
 * Workflows Sidebar
 * 
 * Shows available workflows that can be run from the chat interface.
 * Supports search, category filter, and domain filter.
 * Mobile-responsive with full-screen mode.
 */

import React, { useState } from 'react';
import { 
  Search, 
  X, 
  Play,
  Plus,
  Clock,
  Zap,
  Workflow as WorkflowIcon,
  MapPin,
  Briefcase,
  BarChart2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@flowstate/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from '@/components/ui/sonner';
import { useWorkflows } from '../hooks/useWorkflows';
import { useSubmitWorkflowRun } from '../hooks/useSubmitWorkflowRun';
import type { WorkflowCategory, WorkflowIconKey } from '@flowstate/types';

interface WorkflowsSidebarProps {
  onWorkflowRun: (workflowId: string) => void;
  onWorkflowCreate?: () => void;
  onClose: () => void;
  isMobile?: boolean;
}

const ICON_MAP: Record<WorkflowIconKey, React.ComponentType<{ className?: string }>> = {
  clock: Clock,
  zap: Zap,
  workflow: WorkflowIcon,
  briefcase: Briefcase,
  'bar-chart': BarChart2,
};

const categoryColors = {
  automation: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  analysis: 'bg-primary/10 text-primary border-primary/20',
  reporting: 'bg-green-500/10 text-green-600 border-green-500/20',
  integration: 'bg-orange-500/10 text-orange-600 border-orange-500/20',
};

export const WorkflowsSidebar: React.FC<WorkflowsSidebarProps> = ({
  onWorkflowRun,
  onWorkflowCreate,
  onClose,
  isMobile = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);

  const { workflows, availableDomains } = useWorkflows({
    category: (selectedCategory as WorkflowCategory) ?? undefined,
    domain: selectedDomain ?? undefined,
    search: searchQuery || undefined,
  });

  const { submitWorkflowRun } = useSubmitWorkflowRun();

  const handleClearFilters = () => {
    setSelectedCategory(null);
    setSelectedDomain(null);
  };

  // 1-click: run workflow and close sidebar
  const handleWorkflowRun = async (workflowId: string) => {
    const result = await submitWorkflowRun(workflowId);
    if (result.error) {
      toast.error("Failed to start workflow", { description: result.error });
      return;
    }
    toast.success("⚡ Workflow queued", { description: "Check Background Jobs for progress." });
    onWorkflowRun(workflowId);
    onClose();
  };

  return (
    <div className={cn(
      "bg-muted/30 backdrop-blur-sm border-l flex flex-col h-full",
      isMobile && "absolute inset-0 z-50 w-full"
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-background">
        <div>
          <h2 className="text-lg font-semibold">Workflows</h2>
          <p className="text-xs text-muted-foreground">
            {workflows.length} available
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Search & Create */}
      <div className="p-4 space-y-3 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search workflows..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
        
        {onWorkflowCreate && (
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full justify-start"
            onClick={onWorkflowCreate}
          >
            <Plus className="mr-2 h-4 w-4" /> Create Workflow
          </Button>
        )}

        {/* Category filters */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCategory === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(null)}
            className="h-7 text-xs"
          >
            All
          </Button>
          {(['automation', 'analysis', 'reporting', 'integration'] as const).map(cat => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              className="h-7 text-xs capitalize"
            >
              {cat}
            </Button>
          ))}
        </div>

        {/* Domain filters */}
        {availableDomains.length > 1 && (
          <div className="flex flex-wrap gap-1">
            <div className="flex items-center gap-1 mr-1 text-[10px] text-muted-foreground">
              <MapPin className="h-3 w-3" />
              <span>Domain:</span>
            </div>
            <Button
              variant={selectedDomain === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedDomain(null)}
              className="h-6 text-[10px] px-2"
            >
              All
            </Button>
            {availableDomains.map(domain => (
              <Button
                key={domain}
                variant={selectedDomain === domain ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedDomain(domain)}
                className="h-6 text-[10px] px-2 capitalize"
              >
                {domain}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Workflows List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {workflows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                <WorkflowIcon className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-medium mb-1">No workflows found</h3>
              <p className="text-xs text-muted-foreground max-w-xs">
                Try adjusting your search or filters
              </p>
              {(selectedCategory || selectedDomain) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </Button>
              )}
            </div>
          ) : (
            workflows.map(workflow => {
              const Icon = ICON_MAP[workflow.iconKey] ?? WorkflowIcon;
              return (
                <div
                  key={workflow.id}
                  className="group p-3 rounded-lg border bg-background hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => handleWorkflowRun(workflow.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleWorkflowRun(workflow.id); }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h3 className="font-medium text-sm truncate">
                          {workflow.name}
                        </h3>
                        <div className="flex items-center gap-1 shrink-0">
                          {workflow.domain && (
                            <Badge variant="outline" className="text-[9px] px-1 h-4 font-normal capitalize">
                              {workflow.domain}
                            </Badge>
                          )}
                          <Badge 
                            variant="secondary" 
                            className={cn("text-xs", categoryColors[workflow.category])}
                          >
                            {workflow.category}
                          </Badge>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mb-2">
                        {workflow.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-muted-foreground">
                          {workflow.stepCount} steps
                        </div>
                        
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={(e) => { e.stopPropagation(); handleWorkflowRun(workflow.id); }}
                                className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <Play className="h-3.5 w-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Run workflow</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default WorkflowsSidebar;
